import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, TextArea } from "./LeadsPage";
import { Plus, Pencil, Trash2, Mail, Phone, MapPin, Car, Briefcase, LayoutGrid, List, Rows3 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import ContactJobHistory from "../components/ContactJobHistory";

const blank = { name: "", email: "", phone: "", address: "", vehicle: "", notes: "" };
const VIEW_STORAGE = "wc.contacts.view";
const VIEW_MODES = [
  { key: "wide",  Icon: LayoutGrid, label: "Wide cards",  gridCls: "grid sm:grid-cols-2 lg:grid-cols-3 gap-4" },
  { key: "large", Icon: Rows3,      label: "Large list",  gridCls: "grid grid-cols-1 gap-3" },
  { key: "small", Icon: List,       label: "Small list",  gridCls: "grid grid-cols-1 gap-1.5" },
];

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [query, setQuery] = useState("");
  const [historyFor, setHistoryFor] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "wide";
    return localStorage.getItem(VIEW_STORAGE) || "wide";
  });
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");

  useEffect(() => { localStorage.setItem(VIEW_STORAGE, viewMode); }, [viewMode]);

  const load = async () => setItems((await api.get("/contacts")).data);
  useEffect(() => { load(); }, []);

  // Scroll highlighted contact into view when arriving via ?highlight=<id>
  useEffect(() => {
    if (!highlightId || items.length === 0) return;
    const el = document.querySelector(`[data-testid="contact-card-${highlightId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-white/50");
      const t = setTimeout(() => el.classList.remove("ring-2", "ring-white/50"), 2500);
      // Auto-open the job history so linking straight from a job card takes
      // the user right into the Contact > Job view.
      setHistoryFor(highlightId);
      return () => clearTimeout(t);
    }
  }, [highlightId, items]);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/contacts/${editing.id}`, form);
      else await api.post("/contacts", form);
      toast.success(editing ? "Contact updated" : "Contact created");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contact deleted");
    load();
  };

  const filtered = items.filter((c) =>
    [c.name, c.email, c.phone].some((f) => (f || "").toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label">Address book</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Contacts</h1>
        </div>
        <div className="flex items-center gap-3">
          <input
            data-testid="contact-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
          <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-0.5" data-testid="contacts-view-toggle">
            {VIEW_MODES.map(({ key, Icon, label }) => (
              <button
                key={key}
                type="button"
                data-testid={`contacts-view-${key}`}
                title={label}
                onClick={() => setViewMode(key)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                  viewMode === key ? "bg-white text-black" : "text-slate-300 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          <button data-testid="contacts-create-button" onClick={openCreate} className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 transition">
            <Plus className="w-4 h-4" /> New contact
          </button>
        </div>
      </div>

      <div className={VIEW_MODES.find((m) => m.key === viewMode)?.gridCls || VIEW_MODES[0].gridCls} data-testid={`contacts-grid-${viewMode}`}>
        {filtered.length === 0 && (
          <GlassPanel className="col-span-full !p-8 text-center text-slate-500 italic" delay={0.05}>
            No contacts found.
          </GlassPanel>
        )}
        {viewMode === "small" && filtered.map((c, i) => (
          <SmallRow key={c.id} c={c} i={i} onOpen={() => setHistoryFor(c.id)} onEdit={() => openEdit(c)} onDelete={() => remove(c.id)} />
        ))}
        {viewMode === "large" && filtered.map((c, i) => (
          <LargeRow key={c.id} c={c} i={i} onOpen={() => setHistoryFor(c.id)} onEdit={() => openEdit(c)} onDelete={() => remove(c.id)} />
        ))}
        {viewMode === "wide" && filtered.map((c, i) => (
          <WideCard key={c.id} c={c} i={i} onOpen={() => setHistoryFor(c.id)} onEdit={() => openEdit(c)} onDelete={() => remove(c.id)} />
        ))}
      </div>

      {open && (
        <FormModal title={editing ? "Edit contact" : "New contact"} onClose={() => setOpen(false)} onSubmit={save}>
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
          </div>
          <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="contact-address" />
          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="contact-notes" />
        </FormModal>
      )}

      {historyFor && (
        <ContactJobHistory contactId={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}

/* ---------------- Row variants ---------------- */

function ActionCluster({ c, onOpen, onEdit, onDelete, alwaysVisible = false }) {
  const cls = alwaysVisible
    ? "flex gap-1"
    : "flex gap-1 opacity-0 group-hover:opacity-100 transition";
  return (
    <div className={cls} onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={`contact-history-${c.id}`}
        onClick={onOpen}
        title="View job history"
        className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-300"
      >
        <Briefcase className="w-3.5 h-3.5" />
      </button>
      <button data-testid={`contact-edit-${c.id}`} onClick={onEdit} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
      <button data-testid={`contact-delete-${c.id}`} onClick={onDelete} className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function WideCard({ c, i, onOpen, onEdit, onDelete }) {
  return (
    <GlassPanel
      className="!p-5 group cursor-pointer hover:border-white/20 transition"
      delay={Math.min(i * 0.03, 0.4)}
      data-testid={`contact-card-${c.id}`}
      onClick={onOpen}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-heading text-xl text-white">{c.name}</div>
          {c.address && (
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" /> {c.address}
            </div>
          )}
        </div>
        <ActionCluster c={c} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div className="space-y-1.5 text-sm">
        {c.email && <div className="flex items-center gap-2 text-slate-300"><Mail className="w-3.5 h-3.5 text-slate-500" /> {c.email}</div>}
        {c.phone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-3.5 h-3.5 text-slate-500" /> {c.phone}</div>}
        {c.vehicle && <div className="flex items-center gap-2 text-slate-300"><Car className="w-3.5 h-3.5 text-slate-500" /> {c.vehicle}</div>}
      </div>
      {c.notes && <div className="mt-3 text-xs text-slate-400 line-clamp-2">{c.notes}</div>}
    </GlassPanel>
  );
}

function LargeRow({ c, i, onOpen, onEdit, onDelete }) {
  return (
    <GlassPanel
      className="!p-4 group cursor-pointer hover:border-white/20 transition"
      delay={Math.min(i * 0.02, 0.3)}
      data-testid={`contact-card-${c.id}`}
      onClick={onOpen}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-200 font-heading text-sm shrink-0">
          {(c.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-lg text-white truncate">{c.name}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400 mt-0.5">
            {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
            {c.vehicle && <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {c.vehicle}</span>}
            {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.address}</span>}
          </div>
        </div>
        <ActionCluster c={c} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </GlassPanel>
  );
}

function SmallRow({ c, i, onOpen, onEdit, onDelete }) {
  return (
    <div
      data-testid={`contact-card-${c.id}`}
      className="flex items-center gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl px-3 py-2 cursor-pointer transition group"
      onClick={onOpen}
    >
      <div className="text-white text-sm font-medium truncate w-40 shrink-0">{c.name}</div>
      <div className="text-xs text-slate-400 truncate flex-1 min-w-0">
        {[c.email, c.phone, c.vehicle].filter(Boolean).join(" · ")}
      </div>
      <ActionCluster c={c} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} alwaysVisible />
    </div>
  );
}
